import React, { useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react-lite';
import useStore from '../../services/hooks/useStore';
import { Modal, ModalHeader, ModalBody, Footer } from './styles';
import Botao from '../Botoes/Botao';
import { TextField, Grid } from '@material-ui/core';
import { apiV2 } from '../../services/utils';
import Formatter from '../../services/Formatter';
import X9Logger from '../../services/X9Logger';
import Select from '../Select';
import { abrirDanfePreview } from '../DanfePreview';
import { useHistory } from 'react-router-dom';

type CFOPOption = {
  label: string;
  value: string;
  id: string;
  descricao: string;
};

function formatarDataSaidaEntradaApi(val: unknown): string {
  if (val == null || val === '') return '-';
  const s = String(val).trim();
  if (!s) return '-';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return s;
}

type PreEmissaoNFe = {
  cliente: {
    id: number;
    nome: string;
    cpf_cnpj: string;
  };
  itens: Array<{
    item: number;
    xname?: string;
    descricao: string;
    ncm: string;
    cst: string;
    cfop: string;
    un: string;
    quantidade: number;
    valor_unitario: number;
    valor_total: number;
  }>;
  valor_total: number;
  informacoes_complementares?: string;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  /**
   * Conforme POST /v2/nfe/pre-emissao/ (formData): `movimentacao` + movimentacao_id ou `venda` + venda_id.
   */
  preEmissaoOrigem?: 'movimentacao' | 'venda';
  /** ID da movimentação (pk ou uuid, conforme API) ou ID numérico da venda quando origem é `venda`. */
  preEmissaoContextId: string;
  categoriaFinanceiraId?: number;
  /** Sobrescreve o padrão da pré-emissão (ex.: venda gerada com financeiro em Movimentações). */
  preEmissaoFormaPagamento?: string;
  preEmissaoIndicadorPagamento?: string;
  /** Chamado após emissão com sucesso; `notaFiscalId` permite atualizar status na listagem. */
  onSuccess: (meta?: { notaFiscalId: string }) => void;
};

const ModalNFe: React.FC<Props> = ({
  open,
  handleClose,
  preEmissaoOrigem = 'movimentacao',
  preEmissaoContextId,
  categoriaFinanceiraId,
  preEmissaoFormaPagamento,
  preEmissaoIndicadorPagamento,
  onSuccess,
}) => {
  const store = useStore();
  const history = useHistory();
  const [preEmissao, setPreEmissao] = useState<PreEmissaoNFe | null>(null);
  const [loading, setLoading] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const [cfopOptions, setCfopOptions] = useState<CFOPOption[]>([]);
  const [notaFiscalId, setNotaFiscalId] = useState<string | null>(null);
  const [jsonContent, setJsonContent] = useState<any>(null);
  /** Exibição de `data_saida_ent` retornada pela pré-emissão (e fallbacks no JSON). */
  const [dataSaidaEntradaExibicao, setDataSaidaEntradaExibicao] = useState<string>('-');
  /** Número da NFe exibido conforme `proximo_numero` retornado pela API de pré-emissão. */
  const [proximoNumeroNfe, setProximoNumeroNfe] = useState<string | null>(null);

  const state = useLocalStore(() => ({
    informacoesComplementares: '',
    chaveAcessoReferenciada: '',
    itensEditados: [] as Array<{ item: number; cfop: string }>,
    incluirNomeEspecie: false,
  }));

  const debugLog = (...args: any[]) => {
    console.log('[ModalNFe]', ...args);
  };

  /** Durante a digitação: não usar trim (senão o espaço entre palavras some até o próximo caractere). */
  function normalizeInformacoesComplementaresForInput(value: string): string {
    return (value || '')
      .replace(/\\n/g, ' ')
      .replace(/[\r\n]+/g, ' ');
  }

  /** Na emissão / valor inicial: normaliza espaços e remove bordas. */
  function sanitizeInformacoesComplementaresForEmit(value: string): string {
    return normalizeInformacoesComplementaresForInput(value)
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  useEffect(() => {
    if (open && preEmissaoContextId) {
      debugLog('useEffect disparado', {
        open,
        preEmissaoOrigem,
        preEmissaoContextId,
        preEmissaoFormaPagamento,
        preEmissaoIndicadorPagamento,
      });
      carregarCFOPOptions();
      carregarPreEmissao();
    }
  }, [open, preEmissaoContextId, preEmissaoOrigem, preEmissaoFormaPagamento, preEmissaoIndicadorPagamento]);

  function montarBodyPreEmissaoFormUrlEncoded(): string | null {
    debugLog('Montando payload pre-emissao', {
      preEmissaoOrigem,
      preEmissaoContextId,
      preEmissaoFormaPagamento,
      preEmissaoIndicadorPagamento,
    });
    const params = new URLSearchParams();
    params.set('origem_nota', preEmissaoOrigem);
    params.set('forma_pagamento', preEmissaoFormaPagamento ?? '01');
    params.set('indicador_pagamento', preEmissaoIndicadorPagamento ?? '0');
    if (preEmissaoOrigem === 'venda') {
      const vid = Number(preEmissaoContextId);
      if (!Number.isFinite(vid)) {
        return null;
      }
      params.set('venda_id', String(vid));
    } else {
      params.set('movimentacao_id', preEmissaoContextId);
    }
    debugLog('Payload pre-emissao montado', params.toString());
    return params.toString();
  }

  async function carregarInformacaoComplementarCategoria(): Promise<{ informacaoComplementar: string; incluirNomeEspecie: boolean }> {
    if (!categoriaFinanceiraId) return { informacaoComplementar: '', incluirNomeEspecie: false };
    try {
      debugLog('Carregando informacao complementar da categoria', { categoriaFinanceiraId });
      const res = await apiV2.get('/categoria-nfe/', {
        params: { prop: store.propriedade?.id },
      });
      const categorias = res.data?.results || res.data || [];
      const categoria = categorias.find((c: any) => c.categoria_financeira === categoriaFinanceiraId);
      return {
        informacaoComplementar: (categoria?.informacao_complementar as string) || '',
        incluirNomeEspecie: Boolean(
          (categoria as any)?.inserir_especie ?? (categoria as any)?.incluir_nome_especie,
        ),
      };
    } catch (error) {
      debugLog('Erro ao carregar categoria-nfe', error);
      X9Logger.report(error as Error);
      return { informacaoComplementar: '', incluirNomeEspecie: false };
    }
  }

  // Helper function to extract CST/CSOSN from ICMS structure dynamically
  function getICMSCst(icmsObj: any): string {
    if (!icmsObj || typeof icmsObj !== 'object') return '';

    // Get the first property inside ICMS (could be ICMS00, ICMSSN102, icms00, etc.)
    const firstKey = Object.keys(icmsObj)[0];
    if (!firstKey) return '';

    const icmsData = icmsObj[firstKey];
    if (!icmsData || typeof icmsData !== 'object') return '';

    // Search for CST/CSOSN case-insensitively
    const keys = Object.keys(icmsData);
    const cstKey = keys.find(k => k.toLowerCase() === 'cst' || k.toLowerCase() === 'csosn');

    return cstKey ? icmsData[cstKey] : '';
  }

  async function carregarCFOPOptions() {
    try {
      debugLog('Carregando opcoes de CFOP');
      const cfopResponse = await apiV2.get('/cfop-nfe/');
      const cfopData = cfopResponse.data.map((cfop: any) => ({
        label: `${cfop.n_cfop} - ${cfop.natureza_operacao}`,
        value: cfop.n_cfop,
        id: cfop.id.toString(),
        descricao: cfop.natureza_operacao,
      }));
      setCfopOptions(cfopData);
      debugLog('CFOP carregado', { quantidade: cfopData.length });
    } catch (error) {
      debugLog('Erro ao carregar CFOP', error);
      X9Logger.report(error as Error);
    }
  }

  async function carregarPreEmissao() {
    setLoading(true);
    setErros([]);
    setDataSaidaEntradaExibicao('-');
    setProximoNumeroNfe(null);
    try {
      const bodyPre = montarBodyPreEmissaoFormUrlEncoded();
      debugLog('Body pre-emissao pronto', bodyPre);
      if (bodyPre === null) {
        setErros(['Id da venda inválido para pré-emissão da NFe.']);
        debugLog('Body pre-emissao invalido (null)');
        return;
      }
      // POST /api/v2/nfe/pre-emissao/ — parâmetros em formData (Swagger: v2_nfe_pre_emitir)
      debugLog('POST /nfe/pre-emissao iniciado');
      const response = await apiV2.post(`/nfe/pre-emissao/?prop=${store.propriedade?.id}`, bodyPre, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const data = response.data;
      debugLog('Resposta /nfe/pre-emissao recebida', data);

      // Extract nota_fiscal_id and json_content from response
      setNotaFiscalId(data.nota_fiscal_id?.toString() || null);
      setJsonContent(data.json_content || null);

      const pn = (data as any).proximo_numero ?? (data as any).proximoNumero;
      setProximoNumeroNfe(pn != null && String(pn).trim() !== '' ? String(pn).trim() : null);

      const ide = data.json_content?.infNFe?.ide;
      const dse =
        (data as any).data_saida_ent ??
        (data as any).dataSaidaEnt ??
        ide?.dhSaiEnt ??
        (ide as any)?.dhSaidaEnt;
      setDataSaidaEntradaExibicao(formatarDataSaidaEntradaApi(dse));
      debugLog('notaFiscalId/jsonContent atualizados', {
        notaFiscalId: data.nota_fiscal_id?.toString() || null,
        hasJsonContent: Boolean(data.json_content),
      });

      // The API might return different structures, check for both:
      // 1. Old structure with itens array
      // 2. New structure with json_content.infNFe.det array
      let itensArray = data.itens;

      // If no itens array but json_content exists, extract from json_content
      if (!itensArray && data.json_content?.infNFe?.det) {
        itensArray = data.json_content.infNFe.det.map((det: any, index: number) => ({
          item: index,
          descricao: det.prod?.xProd || '',
          xname: (det.prod as any)?.xNome || (det.prod as any)?.xName || (det.prod as any)?.xname,
          ncm: det.prod?.NCM || '',
          cfop: det.prod?.CFOP || '',

          un: det.prod?.uCom || '',
          quantidade: parseFloat(det.prod?.qCom || '0'),
          valor_unitario: parseFloat(det.prod?.vUnCom || '0'),
          valor_total: parseFloat(det.prod?.vProd || '0'),
          cst: getICMSCst(det.imposto?.ICMS), // Extract CST/CSOSN dynamically
        }));
        debugLog('Itens extraidos do json_content.det', { quantidade: itensArray.length });
      }

      // Garante que itens do fluxo antigo tenham `xname` caso exista.
      if (Array.isArray(itensArray)) {
        itensArray = itensArray.map((it: any) => ({
          ...it,
          xname: it?.xname || it?.xNome || it?.xName,
        }));
      }
      debugLog('Itens finais pre-emissao', { quantidade: Array.isArray(itensArray) ? itensArray.length : 0 });

      const informacoesServidor =
        data.informacoes_complementares || data.json_content?.infNFe?.infAdic?.infCpl || '';
      const informacaoCategoria = await carregarInformacaoComplementarCategoria();

      setPreEmissao({
        cliente: data.cliente || {
          id: 0,
          nome: data.json_content?.infNFe?.dest?.xnome || '',
          cpf_cnpj: data.json_content?.infNFe?.dest?.cpf || data.json_content?.infNFe?.dest?.cnpj || '',
        },
        itens: itensArray || [],
        valor_total: parseFloat(data.json_content?.infNFe?.total?.ICMSTot?.vNF || data.valor_total || '0'),
        informacoes_complementares: informacoesServidor,
      });

      state.incluirNomeEspecie = informacaoCategoria.incluirNomeEspecie;
      state.informacoesComplementares = sanitizeInformacoesComplementaresForEmit(
        informacaoCategoria.informacaoComplementar || informacoesServidor,
      );

      // Initialize edited items with CFOP codes
      state.itensEditados = (itensArray || []).map((item: any) => ({
        item: item.item,
        cfop: item.cfop,
      }));
      debugLog('Pre-emissao carregada com sucesso');
    } catch (error: any) {
      debugLog('Erro em carregarPreEmissao', error?.response?.data || error);
      X9Logger.report(error as Error);
      setDataSaidaEntradaExibicao('-');
      setProximoNumeroNfe(null);
      if (error.response?.data?.erros) {
        setErros(error.response.data.erros);
      } else {
        setErros(['Erro ao carregar dados da NFe. Verifique se todos os dados necessários estão preenchidos.']);
      }
    } finally {
      setLoading(false);
    }
  }

  function atualizarCFOP(item: number, novoCFOP: string) {
    const index = state.itensEditados.findIndex((i) => i.item === item);
    if (index !== -1) {
      state.itensEditados[index].cfop = novoCFOP;
    } else {
      state.itensEditados.push({ item, cfop: novoCFOP });
    }
  }

  function obterCFOP(item: number): string {
    const itemEditado = state.itensEditados.find((i) => i.item === item);
    if (itemEditado) {
      return itemEditado.cfop;
    }
    const itemOriginal = preEmissao?.itens.find((i) => i.item === item);
    return itemOriginal?.cfop || '';
  }

  /** JSON da NFe com CFOP, descrições, infCpl e NFref conforme o formulário (preview e emissão). */
  function construirJsonContentComEdicoesDoUsuario(informacoesComplementaresSanitizadas: string): any | null {
    if (!jsonContent || !preEmissao) return null;
    const updatedJsonContent = JSON.parse(JSON.stringify(jsonContent));

    if (updatedJsonContent.infNFe?.det && Array.isArray(updatedJsonContent.infNFe.det)) {
      updatedJsonContent.infNFe.det = updatedJsonContent.infNFe.det.map((det: any, index: number) => {
        const itemEditado = state.itensEditados.find((item) => item.item === index);
        if (!det.prod) return det;

        const newProd = { ...det.prod };

        if (itemEditado?.cfop) {
          newProd.CFOP = itemEditado.cfop;
        }

        if (state.incluirNomeEspecie) {
          const itemPre = preEmissao.itens?.find((i) => i.item === index);
          const xname = itemPre?.xname || '';
          const descricaoBase = itemPre?.descricao || newProd.xProd || '';

          if (xname && descricaoBase) {
            const descricaoBaseLower = String(descricaoBase).toLowerCase();
            const xnameLower = String(xname).toLowerCase();

            if (!descricaoBaseLower.includes(xnameLower)) {
              newProd.xProd = `${descricaoBase} / ${xname}`;
            }
          }
        }

        return {
          ...det,
          prod: newProd,
        };
      });
    }

    if (informacoesComplementaresSanitizadas && updatedJsonContent.infNFe) {
      if (!updatedJsonContent.infNFe.infAdic) {
        updatedJsonContent.infNFe.infAdic = {};
      }
      updatedJsonContent.infNFe.infAdic.infCpl = informacoesComplementaresSanitizadas;
    }

    if (state.chaveAcessoReferenciada && state.chaveAcessoReferenciada.length === 44 && updatedJsonContent.infNFe) {
      updatedJsonContent.infNFe.NFref = [{ refNFe: state.chaveAcessoReferenciada }];
    }

    return updatedJsonContent;
  }

  async function prepararEmissao() {
    debugLog('prepararEmissao iniciado', {
      hasPreEmissao: Boolean(preEmissao),
      notaFiscalId,
      hasJsonContent: Boolean(jsonContent),
      preEmissaoOrigem,
      preEmissaoContextId,
    });
    if (!preEmissao || !notaFiscalId || !jsonContent) {
      store.notificar('Dados da NFe incompletos. Tente novamente.');
      debugLog('prepararEmissao abortado: dados incompletos');
      return;
    }

    const informacoesComplementaresSanitizadas = sanitizeInformacoesComplementaresForEmit(state.informacoesComplementares);

    if (informacoesComplementaresSanitizadas.length > 500) {
      store.notificar('Informações complementares não podem exceder 500 caracteres.');
      debugLog('prepararEmissao abortado: informacoesComplementares > 500');
      return;
    }

    if (state.chaveAcessoReferenciada.length > 0 && state.chaveAcessoReferenciada.length !== 44) {
      store.notificar('A chave de acesso referenciada deve conter exatamente 44 caracteres.');
      debugLog('prepararEmissao abortado: chave acesso invalida', state.chaveAcessoReferenciada.length);
      return;
    }

    setLoading(true);
    setErros([]);

    try {
      const updatedJsonContent = construirJsonContentComEdicoesDoUsuario(informacoesComplementaresSanitizadas);
      if (!updatedJsonContent) {
        store.notificar('Dados da NFe incompletos. Tente novamente.');
        debugLog('prepararEmissao abortado: construirJsonContent retornou null');
        return;
      }
      debugLog('jsonContent montado com edicoes do usuario para emissao');

      // POST /api/v2/nfe/emitir/ — formData (Swagger: v2_nfe_emitir)
      const emitParams = new URLSearchParams();
      emitParams.set('origem_nota', preEmissaoOrigem);
      emitParams.set('nota_fiscal_id', notaFiscalId);
      emitParams.set('json_content', JSON.stringify(updatedJsonContent));

      if (preEmissaoOrigem === 'venda') {
        emitParams.set('venda_id', String(Number(preEmissaoContextId)));
      } else {
        emitParams.set('movimentacao_id', preEmissaoContextId);
      }

      if (informacoesComplementaresSanitizadas) {
        emitParams.set('complemento', informacoesComplementaresSanitizadas);
      }

      if (state.chaveAcessoReferenciada && state.chaveAcessoReferenciada.length === 44) {
        emitParams.set('chave_acesso', state.chaveAcessoReferenciada);
      }

      debugLog('Payload emissao montado', emitParams.toString());
      debugLog('POST /nfe/emitir iniciado');
      await apiV2.post(`/nfe/emitir/?prop=${store.propriedade?.id}`, emitParams.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      debugLog('POST /nfe/emitir sucesso');

      store.notificar('NFe emitida com sucesso!');
      const idEmitida = notaFiscalId as string;
      onSuccess({ notaFiscalId: idEmitida });
      handleClose();
      history.push({
        pathname: '/nota-fiscal/nfe-emitidas',
        state: { agendarAtualizacaoNfeId: idEmitida },
      });
    } catch (error: any) {
      debugLog('Erro em prepararEmissao', error?.response?.data || error);
      X9Logger.report(error as Error);
      if (error.response?.data?.erros) {
        setErros(Array.isArray(error.response.data.erros) ? error.response.data.erros : [error.response.data.erros]);
      } else {
        setErros(['Erro ao emitir NFe. Tente novamente.']);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader>Gerar Nota Fiscal</ModalHeader>
      <ModalBody style={{ width: '1000px', maxHeight: '85vh', overflowY: 'auto' }}>
        {loading && !preEmissao ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Carregando dados da NFe...</div>
        ) : erros.length > 0 ? (
          <div style={{ padding: '20px' }}>
            <h3 style={{ color: '#F44336', marginBottom: '15px' }}>Dados necessários para emissão:</h3>
            <ul>
              {erros.map((erro, index) => (
                <li key={index} style={{ marginBottom: '8px', color: '#F44336' }}>
                  {erro}
                </li>
              ))}
            </ul>
          </div>
        ) : preEmissao ? (
          <div>
            {/* N. NFe / Série */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>N. NFe / Série</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0288d1' }}>
                {`${proximoNumeroNfe ?? jsonContent?.infNFe?.ide?.nNF ?? notaFiscalId ?? '-'} / ${jsonContent?.infNFe?.ide?.serie || '-'}`}
              </div>
            </div>

            <Grid container spacing={2}>
              {/* Cliente + Natureza da Operação */}
              <Grid item xs={6}>
                <TextField
                  label="Cliente"
                  value={preEmissao.cliente.nome}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Natureza da Operação"
                  value={jsonContent?.infNFe?.ide?.natOp || 'Venda de Mercadoria'}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Data da Emissão + Data saída/entrada (API: data_saida_ent) */}
              <Grid item xs={6}>
                <TextField
                  label="Data da Emissão"
                  value={new Date().toLocaleDateString('pt-BR')}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Data saída/entrada"
                  value={dataSaidaEntradaExibicao}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Valor Total */}
              <Grid item xs={6}>
                <TextField
                  label="Valor Total (R$)"
                  value={`R$ ${Formatter.formatNumber(2, preEmissao.valor_total)}`}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Valor ICMS */}
              <Grid item xs={6}>
                <TextField
                  label="Valor ICMS (R$)"
                  value={`R$ ${Formatter.formatNumber(2, parseFloat(jsonContent?.infNFe?.total?.ICMSTot?.vICMS || '0'))}`}
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Chave de Acesso Referenciada */}
              <Grid item xs={12}>
                <TextField
                  label="Chave de Acesso Referenciada"
                  value={state.chaveAcessoReferenciada}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 44);
                    state.chaveAcessoReferenciada = val;
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={
                    state.chaveAcessoReferenciada.length > 0 && state.chaveAcessoReferenciada.length !== 44
                      ? `${state.chaveAcessoReferenciada.length}/44 caracteres - A chave deve conter exatamente 44 caracteres`
                      : `${state.chaveAcessoReferenciada.length}/44 caracteres`
                  }
                  error={state.chaveAcessoReferenciada.length > 0 && state.chaveAcessoReferenciada.length !== 44}
                  inputProps={{ maxLength: 44 }}
                />
              </Grid>

              {/* Informações Complementares */}
              <Grid item xs={12}>
                <TextField
                  label="Informações Complementares"
                  value={state.informacoesComplementares}
                  onChange={(e) => {
                    const normalized = normalizeInformacoesComplementaresForInput(e.target.value);
                    state.informacoesComplementares = normalized.slice(0, 500);
                  }}
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={`${state.informacoesComplementares.length}/500 caracteres`}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>

              {/* Tabela de Itens */}
              <Grid item xs={12}>
                <div style={{ overflowX: 'auto', marginTop: '10px', position: 'relative' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600' }}>Código</th>

                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600' }}>Descrição do Produto</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600' }}>NCM</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600' }}>CST</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', minWidth: '180px' }}>CFOP</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600' }}>Qtde</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600' }}>Vr Unit</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>Vr Total</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>Vr ICMS</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>Al ICMS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preEmissao.itens.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '8px' }}>{item.item}</td>

                          <td style={{ padding: '8px' }}>
                            {(() => {
                              const descricaoBase = item.descricao || '';
                              const xname = item.xname || '';
                              if (!state.incluirNomeEspecie || !xname) return descricaoBase;

                              const descricaoBaseLower = String(descricaoBase).toLowerCase();
                              const xnameLower = String(xname).toLowerCase();

                              // Evita duplicar caso o backend já tenha concatenado.
                              if (descricaoBaseLower.includes(xnameLower)) return descricaoBase;
                              return `${descricaoBase} / ${xname}`;
                            })()}
                          </td>
                          <td style={{ padding: '8px' }}>{item.ncm}</td>
                          <td style={{ padding: '8px' }}>{item.cst}</td>
                          <td style={{ padding: '4px', position: 'relative', zIndex: 10 }}>
                            <Select
                              value={cfopOptions.find((opt) => opt.value === obterCFOP(item.item)) || null}
                              onChange={(e: any) => {
                                if (e) {
                                  atualizarCFOP(item.item, e.value);
                                }
                              }}
                              placeholder="CFOP"
                              options={cfopOptions}
                              menuPlacement="auto"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                              }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {Formatter.formatNumber(0, item.quantidade)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {Formatter.formatNumber(2, item.valor_unitario)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {Formatter.formatNumber(2, item.valor_total)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {(() => {
                              const icmsObj = jsonContent?.infNFe?.det?.[index]?.imposto?.ICMS;
                              if (!icmsObj) return '0.00';
                              const firstKey = Object.keys(icmsObj)[0];
                              const vICMS = firstKey ? icmsObj[firstKey]?.vICMS : '0';
                              return Formatter.formatNumber(2, parseFloat(vICMS || '0'));
                            })()}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {(() => {
                              const icmsObj = jsonContent?.infNFe?.det?.[index]?.imposto?.ICMS;
                              if (!icmsObj) return '0.00';
                              const firstKey = Object.keys(icmsObj)[0];
                              const pICMS = firstKey ? icmsObj[firstKey]?.pICMS : '0';
                              return Formatter.formatNumber(2, parseFloat(pICMS || '0'));
                            })()}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Grid>
            </Grid>
          </div>
        ) : null}
      </ModalBody>
      <Footer>
        <Botao onClick={handleClose} variant="contained" cor="#f44336" disabled={loading}>
          Cancelar
        </Botao>
       
        {preEmissao && jsonContent?.infNFe && (
          <Botao
            onClick={() => {
              const infCpl = sanitizeInformacoesComplementaresForEmit(state.informacoesComplementares);
              const atualizado = construirJsonContentComEdicoesDoUsuario(infCpl);
              if (atualizado?.infNFe) {
                abrirDanfePreview(atualizado.infNFe, notaFiscalId || '-');
              }
            }}
            variant="contained"
            cor="#ff9800"
            disabled={loading || !jsonContent}
          >
            Visualizar NFE
          </Botao>
        )}
        {preEmissao && (
          <Botao onClick={prepararEmissao} variant="contained" cor="#00C853" disabled={loading}>
            Enviar
          </Botao>
        )}
      </Footer>
    </Modal>
  );
};

export default observer(ModalNFe);

